<template>
  <InteractiveModal
    modalId="barrierModal"
    :modalTitle="isNew ? 'Create Barrier' : 'Edit Barrier'"
    :modalContent="displayModalContent"
    :isModalActive="isModalActive"
    :isMinimized="isMinimized"
    :draggable="draggable"
    :isDirty="isDirty"
    @close="$emit('close')"
    @minimize="$emit('minimize')"
    @restore="$emit('restore')"
  >
    <template slot="modal-body">
      <b-field
        label="Title"
        :message="errorMessage.title"
        :type="errorMessage.title ? 'is-danger' : undefined"
      >
        <b-input
          v-model="dirtyItem.title"
          type="textarea"
          @input="validateData()"
        />
      </b-field>
      <b-field label="Domain/Category">
        <DomainCategoryDropdown
          v-if="!isDomainCategoriesLoading"
          modalTitle="Barrier"
          :isNew="isNew"
          :projectId="projectId"
          :gripDomainCategories="gripDomainCategoryOptions"
          :gripDomainCategoryDetails="gripDomainCategoryDetails"
          @selectedGripDomainCategory="handleSelectedGripDomainCategory"
          @close="$emit('close')"
        />
      </b-field>
      <div>
        <b-field
          label="Description"
          :message="errorMessage.description"
          :type="errorMessage.description ? 'is-danger' : undefined"
          class="width-full display-block-important"
        >
          <b-input
            v-model="dirtyItem.description"
            type="textarea"
            @input="validateData()"
          />
          <span
            :class="[$style.descriptionWordCount, 'align-end', 'display-flex']"
            >{{ wordCount(dirtyItem.description) }}/150 words</span
          >
        </b-field>
      </div>
      <b-field label="Link(s)" class="width-full">
        <div>
          <div v-for="(link, linkIndex) in itemLinks" :key="linkIndex">
            <div class="display-flex margin-top16">
              <b-field
                class="width-100 no-bottom-margin"
                :message="link.errorMessage"
                :type="!link.isValid ? 'is-danger' : undefined"
              >
                <b-input
                  v-model="link.url"
                  :class="[
                    $style.linkInput,
                    'width-100',
                    'bg-white',
                    'align-right',
                  ]"
                  type="text"
                  @input="validateGripLink(linkIndex)"
                >
                </b-input>
              </b-field>
              <a
                :class="[
                  $style.deleteLinkColor,
                  'padding-top12',
                  'margin-left16',
                  'cursor-pointer',
                ]"
                @click="removeLinkTextInput(linkIndex)"
              >
                <b-icon icon="delete" size="is-small"></b-icon>
              </a>
            </div>
            <div v-if="link.url && link.isValid">
              <a target="_blank" :class="$style.sourceLink" :href="sourceLink">
                Go to source link
                <b-icon icon="launch" size="is-small"></b-icon>
              </a>
            </div>
          </div>
          <a
            class="add-participant-text cursor-pointer"
            @click="addLinkTextInput"
          >
            + Add Link
          </a>
        </div>
      </b-field>
      <b-field label="Attachment(s)">
        <GenericFileUploader :fileAttachment="fileAttachment" />
      </b-field>
      <b-checkbox v-model="dirtyItem.action" class="checkbox-size"
        >To be actioned</b-checkbox
      >
      <div v-if="!dirtyItem.action">
        <br />
        <div>
          <b-field
            label="Justification"
            :message="errorMessage.reason"
            :type="errorMessage.reason ? 'is-danger' : undefined"
            class="width-full display-block-important"
          >
            <b-input
              v-model="dirtyItem.reason"
              type="textarea"
              @input="validateData()"
            />
            <span
              :class="[
                $style.descriptionWordCount,
                'align-end',
                'display-flex',
              ]"
              >{{ wordCount(dirtyItem.reason) }}/50 words</span
            >
          </b-field>
        </div>
      </div>
    </template>
    <template slot="modal-footer">
      <button
        v-if="gripBarrierDetails && Object.keys(gripBarrierDetails).length > 0"
        class="button is-primary is-rounded"
        @click="onUpdateGripBarrier()"
      >
        Save
      </button>
      <button
        v-else
        class="button is-primary is-rounded"
        @click="onAddGripBarrier()"
      >
        Create Barrier
      </button>
    </template>
  </InteractiveModal>
</template>

<script lang="ts">
import { Component, Prop } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import GenericFileUploader from '../../../components/uploader/GenericFileUploader.vue';
import {
  CreateGripBarrierBase,
  GripBarrierDTO,
  GripDomainCategory,
  LinkDTO,
  UpdateGripBarrierPayload,
} from '../../../store/types/grip.types';
import DomainCategoryDropdown from '../DomainCategory/DomainCategoryDropdown.vue';
// import InteractiveModal from '@/components/base/InteractiveModal.vue';
import InteractiveModal from '../../../components/base/InteractiveModal.vue';
import GripModalMixin from '@/views/Grip/components/grip-modal.mixin';

@Component({
  components: { DomainCategoryDropdown, GenericFileUploader, InteractiveModal },
})
export default class BarrierModal extends mixins(GripModalMixin) {
  @Prop() public gripBarrierDetails!: GripBarrierDTO;

  get gripDomainCategoryDetails(): GripDomainCategory | null {
    return this.gripBarrierDetails?.gripDomainCategory ?? null;
  }

  public dirtyItem: CreateGripBarrierBase = {
    title: '',
    description: '',
    action: true,
    reason: '',
    links: [],
    attachments: [],
    gripDomainCategoryId: null,
  };
  public itemLinks: LinkDTO[] = [];

  public errorMessage: {
    title: string | null;
    description: string | null;
    reason: string | null;
  } = { title: null, description: null, reason: null };

  public validateData() {
    if (!this.dirtyItem.title.trim().length) {
      this.errorMessage.title = 'This Field is Required';
    } else {
      this.errorMessage.title = null;
    }

    if (
      this.dirtyItem.description &&
      this.wordCount(this.dirtyItem.description.trim()) > 150
    ) {
      this.errorMessage.description = 'Max 150 words are allowed.';
    } else {
      this.errorMessage.description = null;
    }

    if (!this.dirtyItem.action) {
      if (this.dirtyItem.reason && this.dirtyItem.reason.trim().length) {
        if (
          this.dirtyItem.reason &&
          this.wordCount(this.dirtyItem.reason.trim()) <= 50
        ) {
          this.errorMessage.reason = null;
        } else {
          this.errorMessage.reason = 'Max 50 words are allowed.';
        }
      } else {
        this.errorMessage.reason = 'This Field is Required';
      }
    } else {
      this.errorMessage.reason = null;
    }
    return this.errorMessage;
  }

  public onAddGripBarrier(): void {
    this.validateData();
    if (
      !this.errorMessage.title &&
      !this.errorMessage.description &&
      !this.errorMessage.reason &&
      this.areLinksValid
    ) {
      this.getGripItemLinks();
      this.getItemAttachments();
      this.dirtyItem.gripDomainCategoryId = this.selectedGripDomainCategoryId;
      this.$emit('create', this.dirtyItem);
    }
  }

  public onUpdateGripBarrier(): void {
    this.getGripItemLinks();
    this.getItemAttachments();
    this.dirtyItem.gripDomainCategoryId = this.selectedGripDomainCategoryId;
    const barrier: { [key: string]: any } = { ...this.dirtyItem };
    const barrierInfo: { [key: string]: any } = {
      ...this.gripBarrierDetails,
    };

    let updateBarrier: UpdateGripBarrierPayload | any = {};
    Object.keys(barrier).forEach((key: string) => {
      if (key === 'gripDomainCategoryId') {
        if (barrierInfo.gripDomainCategory) {
          if (
            JSON.stringify(barrier.gripDomainCategoryId) !==
            JSON.stringify(barrierInfo.gripDomainCategory.id)
          ) {
            updateBarrier.gripDomainCategoryId = barrier.gripDomainCategoryId;
          }
        } else if (
          barrierInfo.gripDomainCategory !== barrier.gripDomainCategoryId
        ) {
          updateBarrier.gripDomainCategoryId = barrier.gripDomainCategoryId;
        }
      } else {
        if (JSON.stringify(barrier[key]) !== JSON.stringify(barrierInfo[key])) {
          updateBarrier = {
            ...updateBarrier,
            [key]: barrier[key],
          };
        }
      }
    });
    this.validateData();
    if (
      !this.errorMessage.title &&
      !this.errorMessage.description &&
      !this.errorMessage.reason &&
      this.areLinksValid
    ) {
      /**
       * If validation passes, always emit 'update'.
       * 'updateBarrier' will be an empty object if no actual changes were made
       */
      this.$emit('update', {
        id: this.gripBarrierDetails.id,
        ...updateBarrier,
      });
    }
  }

  public mounted(): void {
    this.isDomainCategoriesLoading = true;
    this.getGripDomainCategories(this.projectId);
    if (
      this.gripBarrierDetails &&
      Object.keys(this.gripBarrierDetails).length > 0
    ) {
      const {
        gripDomainCategory,
        id,
        ...barrierInfo
      } = this.gripBarrierDetails;
      barrierInfo.links.forEach((link: string) => {
        this.count++;
        this.itemLinks.push({
          url: link,
          count: this.count,
          isValid: true,
          errorMessage: '',
        });
      });

      barrierInfo.attachments.forEach((file) => {
        const fileData = file.split('/');
        this.fileAttachment.push({
          type: file,
          name: fileData[fileData.length - 1],
        });
      });

      this.selectedGripDomainCategoryId = gripDomainCategory
        ? gripDomainCategory.id
        : null;

      this.dirtyItem = {
        ...barrierInfo,
        gripDomainCategoryId: this.selectedGripDomainCategoryId,
      };
    }

    this.setInitialFormState();
  }
}
</script>
<style lang="scss" module>
.descriptionWordCount {
  color: $grey;
  font-size: $size-5;
  font-weight: $weight-regular;
  flex-direction: column-reverse; /* This specific flex property remains */
}

.linkInput {
  height: 44px;
  border-color: $grey-border-2 !important;
  color: $dark !important;
}

.deleteLinkColor {
  color: $secondary;
}

.sourceLink {
  color: $secondary;
  font-size: $size-6;
  font-weight: $weight-bold;
}
</style>
